/**
 * First-visit cookie consent banner.
 *
 * Shows when no consent has been recorded yet. Accept calls the
 * GA4 + AdSense initialisers (which inject their script tags); Reject
 * just records the decision so subsequent loads skip the banner.
 *
 * Either choice persists to localStorage via `lib/consent`. Until the
 * user picks something, no third-party scripts have loaded — that's
 * the point: GDPR-compliant default-off behaviour. Visitors who never
 * choose still see the site; they just see neither ads nor analytics.
 *
 * The footer link in App.tsx wires `clearConsent + reload` to bring
 * this banner back when the user wants to change their mind.
 */

import { Cookie } from 'lucide-react';

import { setConsent, type ConsentValue } from '../lib/consent';
import { initAdsense } from '../lib/ads';
import { initAnalytics } from '../lib/analytics';
import { t, type UiLangCode } from '../lib/i18n';

interface Props {
  lang: UiLangCode;
  onDismiss: () => void;
}

export function CookieBanner({ lang, onDismiss }: Props): JSX.Element {
  const choose = (value: ConsentValue): void => {
    setConsent(value);
    if (value === 'accepted') {
      // Inject gtag.js + adsbygoogle.js now that we have permission.
      // Both are no-ops if their respective VITE_* vars are unset.
      initAnalytics();
      initAdsense();
    }
    onDismiss();
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 shadow-lg backdrop-blur dark:border-slate-800 dark:bg-slate-900/95"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
        <Cookie
          className="h-5 w-5 flex-shrink-0 text-indigo-600 dark:text-indigo-400 sm:h-6 sm:w-6"
          aria-hidden="true"
        />
        <p className="flex-1 text-sm text-slate-700 dark:text-slate-300">
          {t(lang, 'consent.message')}{' '}
          <a
            href="/privacy"
            className="underline-offset-4 hover:text-slate-900 hover:underline dark:hover:text-slate-100"
          >
            {t(lang, 'consent.privacy')}
          </a>
        </p>
        <div className="flex gap-2 sm:flex-shrink-0">
          <button
            type="button"
            onClick={() => choose('rejected')}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {t(lang, 'consent.reject')}
          </button>
          <button
            type="button"
            onClick={() => choose('accepted')}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            {t(lang, 'consent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
